import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Settings, CreditCard, Bell, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SellerProfile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: userData } = useQuery({
    queryKey: ['seller-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      return {
        ...profile,
        email: user.email
      };
    }
  });

  const handleFoodieModeToggle = () => {
    navigate('/profile');
    toast({
      title: "Switched to Foodie Mode",
      description: "You are now in Foodie Mode",
      duration: 3000,
    });
  };

  const getInitials = () => {
    if (!userData) return '';
    return `${userData.first_name?.[0] || ''}${userData.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };

  const getFullName = () => {
    if (!userData) return 'Loading...';
    return `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'Unnamed User';
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6 max-w-2xl">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-2xl font-semibold text-primary">{getInitials()}</span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{getFullName()}</h1>
          <p className="text-muted-foreground">{userData?.email}</p>
        </div>
      </div>

      <Button 
        variant="outline" 
        className="w-full flex items-center gap-2"
        onClick={handleFoodieModeToggle}
      >
        Switch to Foodie Mode
      </Button>

      <Card className="p-4 space-y-4">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors"
          onClick={() => navigate('/seller/business-profile')}
        >
          <Settings className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <h3 className="font-medium text-black">Business Profile</h3>
            <p className="text-sm text-black">View and manage your business information</p>
          </div>
        </div>

        <div 
          className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors"
          onClick={() => navigate('/seller/payment')}
        >
          <CreditCard className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <h3 className="font-medium text-black">Payment Methods</h3>
            <p className="text-sm text-black">Manage your payout options</p>
          </div>
        </div>

        <div 
          className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors"
          onClick={() => navigate('/seller/notifications')}
        >
          <Bell className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <h3 className="font-medium text-black">Notifications</h3>
            <p className="text-sm text-black">Choose what you want to be notified about</p>
          </div>
        </div>

        <div 
          className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 p-3 rounded-lg transition-colors"
          onClick={() => navigate('/seller/help')}
        >
          <HelpCircle className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <h3 className="font-medium text-black">Help & Support</h3>
            <p className="text-sm text-black">Get help with your seller account</p>
          </div>
        </div>
      </Card>

      <Button 
        variant="outline" 
        className="w-full"
        onClick={async () => {
          await supabase.auth.signOut();
          navigate('/auth');
        }}
      >
        Sign Out
      </Button>
    </div>
  );
};

export default SellerProfile;
